import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { vatReportingApi } from '../../services/vatReportingApi';
import type { ApiError } from '../../services/api';
import { useVatLang } from './VatLang';
import type { ExclusionAction, VatCompany } from '../../types/vatReporting';

/**
 * The comment dialog behind every manual decision.
 *
 * ONE COMPONENT FOR ALL FOUR ACTIONS, because they differ in exactly two things: the sentence
 * above the box and whether a reason is demanded. Four dialogs would be four places for the
 * "the decision covers all three declarations" line to drift out of step.
 *
 * THE REASON IS THE FEATURE, NOT A FORMALITY. Save stays disabled until something is typed for
 * exclude, include and drop - the same rule the NOT NULL column enforces server-side, said
 * earlier so nobody meets it as a 400. `reset` is the exception and takes none: withdrawing a
 * decision restores the state the data already describes, so there is nothing to justify.
 *
 * WHAT IT SHOWS BEFORE IT ASKS. The posting, its Kennzahl and what that Kennzahl will move by.
 * A dialog that only said "are you sure?" would be asking somebody to change a tax figure
 * without telling them by how much.
 */

export interface ExclusionTarget {
    entryNo: number;
    documentNo: string | null;
    counterpartyName: string | null;
    kennzahl: string | null;
    /**
     * What the Kennzahl moves by, sign and line role already applied by the server. Shown as a
     * MAGNITUDE, because the sentence around it already says which way ("sinkt um" / "steigt
     * um") - printing a negative there once produced "sinkt um -99'425.00", which reads as the
     * opposite of what excluding the posting does.
     */
    contribution: number | null;
    /** For include / drop / reset: the month the posting was deferred from. */
    originYear?: number;
    originMonth?: number;
    /** The reason given when it was deferred, shown so a resolution is made in context. */
    originComment?: string;
}

interface Props {
    action: ExclusionAction;
    company: VatCompany;
    /** exclude: the month the posting leaves. include: the month it enters. Ignored otherwise. */
    year: number;
    month: number;
    target: ExclusionTarget;
    onClose: () => void;
    onDone: () => void;
}

const money = (v: number | null) =>
    v === null || v === undefined
        ? '—'
        : new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const periodName = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`;

export function EntryExclusionDialog({
    action, company, year, month, target, onClose, onDone,
}: Props) {
    const { t } = useVatLang();
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const origin = target.originYear && target.originMonth
        ? periodName(target.originYear, target.originMonth) : '—';
    const needsComment = action !== 'reset';
    const canSave = !saving && (!needsComment || comment.trim().length > 0);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, saving]);

    const submit = async () => {
        if (!canSave) return;
        setSaving(true);
        setError(null);
        try {
            const text = comment.trim();
            if (action === 'exclude') {
                await vatReportingApi.excludeEntry({
                    company, year, month, entryNo: target.entryNo, comment: text,
                });
            } else if (action === 'include') {
                await vatReportingApi.includeEntry({
                    company, year, month, entryNo: target.entryNo, comment: text,
                });
            } else if (action === 'drop') {
                await vatReportingApi.dropEntry({
                    company, entryNo: target.entryNo, comment: text,
                });
            } else {
                await vatReportingApi.resetEntryExclusion({ company, entryNo: target.entryNo });
            }
            onDone();
        } catch (e) {
            // Shown INSIDE the dialog rather than on the page behind it: a refusal on a filed
            // period is an answer to the question being asked, and closing the dialog to
            // deliver it would lose what was typed. That refusal gets the page's own sentence,
            // keyed on the code: the server's message is English and the reader may not be.
            const filed = (e as ApiError | undefined)?.code === 'PERIOD_FILED';
            setError(filed
                ? t(`excl.filedRefusal.${action}`, { period: periodName(year, month), origin })
                : e instanceof Error ? e.message : t('excl.failed'));
            setSaving(false);
        }
    };

    /**
     * Which way the figure moves, per action - and NOT a single "rises by" for everything that
     * is not an exclusion, which is where this started.
     *   drop   moves nothing anywhere. The posting was already out of every declaration; the
     *          decision only closes the question, so naming an amount that "rises" would be
     *          plainly false.
     *   reset  moves the ORIGIN month, which is usually not the month on screen. The intro
     *          sentence names that month; the figure here stays directionless rather than
     *          claiming a rise in the month the reader is looking at.
     */
    const effectText = !target.kennzahl
        ? t('excl.effectNone')
        : action === 'drop'
            ? t('excl.effectDrop', { kz: target.kennzahl })
            : t('excl.effectValue', {
                kz: target.kennzahl,
                amount: money(target.contribution === null
                    ? null : Math.abs(target.contribution)),
                sign: action === 'exclude'
                    ? t('excl.signDown')
                    : action === 'include' ? t('excl.signUp') : t('excl.signBack'),
            });

    const intro = t(`excl.intro.${action}`, { period: periodName(year, month), origin });

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
             onClick={() => !saving && onClose()}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col"
                 onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between px-5 py-3 border-b">
                    <h2 className="text-base font-semibold text-gray-900">
                        {t(`excl.title.${action}`)}
                    </h2>
                    <button onClick={onClose} disabled={saving}
                            className="text-gray-400 hover:text-gray-600 p-1 disabled:opacity-40">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-3">
                    <p className="text-sm text-gray-700">{intro}</p>

                    <div className="rounded border bg-gray-50 px-3 py-2 text-sm space-y-1">
                        <div className="flex justify-between gap-4">
                            <span className="text-gray-500">{t('drill.entry')}</span>
                            <span className="font-medium tabular-nums">{target.entryNo}</span>
                        </div>
                        {target.documentNo && (
                            <div className="flex justify-between gap-4">
                                <span className="text-gray-500">{t('drill.document')}</span>
                                <span className="font-medium">{target.documentNo}</span>
                            </div>
                        )}
                        {target.counterpartyName && (
                            <div className="flex justify-between gap-4">
                                <span className="text-gray-500">{t('drill.counterparty')}</span>
                                <span className="truncate">{target.counterpartyName}</span>
                            </div>
                        )}
                        <div className="flex justify-between gap-4">
                            <span className="text-gray-500">{t('excl.effect')}</span>
                            <span className="font-medium tabular-nums">{effectText}</span>
                        </div>
                    </div>

                    {target.originComment && action !== 'exclude' && (
                        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                            <span className="font-medium">{t('excl.originReason')}</span>{' '}
                            {target.originComment}
                        </div>
                    )}

                    {/* Said on every dialog, not once in the documentation: the reach of the
                        decision is the thing a person is most likely to assume wrongly. */}
                    <p className="flex items-start gap-2 text-xs text-gray-500">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        {t('excl.package')}
                    </p>

                    {needsComment && (
                        <label className="block">
                            <span className="block text-sm font-medium text-gray-700 mb-1">
                                {t('excl.commentLabel')}
                                <span className="text-red-600"> *</span>
                            </span>
                            <textarea
                                value={comment}
                                onChange={e => setComment(e.target.value.slice(0, 1000))}
                                rows={3}
                                autoFocus
                                placeholder={t(`excl.placeholder.${action}`)}
                                className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="block text-xs text-gray-400 mt-0.5">
                                {t('excl.commentHint', { left: 1000 - comment.length })}
                            </span>
                        </label>
                    )}

                    {error && (
                        <div className="border border-red-200 bg-red-50 rounded px-3 py-2 text-sm text-red-800">
                            {error}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50">
                    <button onClick={onClose} disabled={saving}
                            className="px-3 py-1.5 text-sm border rounded bg-white hover:bg-gray-50 disabled:opacity-40">
                        {t('excl.cancel')}
                    </button>
                    <button onClick={submit} disabled={!canSave}
                            className={`px-3 py-1.5 text-sm rounded text-white inline-flex items-center gap-1.5 ${
                                action === 'drop'
                                    ? 'bg-red-600 hover:bg-red-700'
                                    : 'bg-blue-600 hover:bg-blue-700'
                            } disabled:opacity-40`}>
                        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        {t(`excl.confirm.${action}`)}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
